/**
 * @file The Insert run state of one Signify fetch job, keyed by job id. Consumer:
 * `useSignifyFetchJobInsert`, whose re-entrancy guard and button label read it.
 * It is held at module scope for the same reason the stores are held above the modal: the modal is
 * `destroyOnHidden`, so closing it mid-run unmounts the progress step while the run carries on. A
 * per-mount proxy would let the reopened modal start a second run over the rows the first has not
 * written yet, and the second run's idempotency query — taken before those writes land — would not
 * see them, putting every remaining part in the table twice.
 * Entries are never evicted: a remount must observe the same proxy the running pass is writing, and
 * the map holds one small object per job the operator opens in a session.
 */

import { proxy } from 'valtio';
import type { SignifyFetchJobInserting } from '../../index.types';

const byJobId = new Map<string, SignifyFetchJobInserting>();

export const signifyFetchJobInsertingFor = (jobId: string): SignifyFetchJobInserting => {
  const held = byJobId.get(jobId);
  if (held !== undefined) return held;

  const created = proxy<SignifyFetchJobInserting>({ phase: 'idle', done: 0, total: 0 });
  byJobId.set(jobId, created);
  return created;
};
